import { getFileMimeType } from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { getImageSource } from '../../../../imageHelper';
import { getLargestImageSize } from '../../../imageModalUtils';
import {
    getBackgroundRemovalEditSource,
    getBackgroundRemovalInactiveSource,
    isBackgroundRemovalActive,
} from '../../toolbar/imageModalBackgroundRemovalUtils';
import { getMimeTypeFromImageUrl } from '../../../../imageUtils';
import { MimeType } from '../../../../../../../common/media/mimeConstants';
import type { ImMNElement, MNImageContent } from '../../../../../../../common/elements/elementModelTypes';

/**
 * Resolve a concrete image URL for one stored variant.
 */
const getVariantImageUrl = ({
    elementId,
    useSecureMediaUrl,
    renderSecureImagesThroughCF,
    variantImage,
}: {
    elementId: string;
    useSecureMediaUrl: boolean;
    renderSecureImagesThroughCF: boolean;
    variantImage: MNImageContent | null | undefined;
}): string | null => {
    if (!variantImage) return null;

    return getImageSource({
        imageDetails: variantImage,
        imageSize: getLargestImageSize(variantImage),
        elementId,
        useSecureMediaUrl,
        renderSecureImagesThroughCF,
    });
};

/**
 * Build the image sources and MIME type needed by the crop flow when background removal is enabled.
 */
export const getBackgroundRemovalCropImageData = ({
    element,
    elementId,
    useSecureMediaUrl,
    renderSecureImagesThroughCF,
}: {
    element: ImMNElement;
    elementId: string;
    useSecureMediaUrl: boolean;
    renderSecureImagesThroughCF: boolean;
}): {
    backgroundRemovalEditSource: MNImageContent | undefined;
    inactiveVariantImageUrl: string | null;
    inactiveVariantImageType: string;
    imageType: string;
} => {
    const backgroundRemovalEditSource = getBackgroundRemovalEditSource(element);
    const isBgRemovalActive = isBackgroundRemovalActive(element);
    const originalImageType = getFileMimeType(element) || MimeType.JPEG;

    const inactiveVariantImageUrl = getVariantImageUrl({
        elementId,
        useSecureMediaUrl,
        renderSecureImagesThroughCF,
        variantImage: getBackgroundRemovalInactiveSource(element),
    });
    const inactiveVariantImageType = inactiveVariantImageUrl
        ? // The hidden side should keep the original provider format when we already have it;
          // otherwise we fall back to PNG so the crop/upload path preserves transparency.
          getMimeTypeFromImageUrl(inactiveVariantImageUrl, isBgRemovalActive ? originalImageType : MimeType.PNG)
        : originalImageType;

    const imageType = backgroundRemovalEditSource
        ? getMimeTypeFromImageUrl(
              getVariantImageUrl({
                  elementId,
                  useSecureMediaUrl,
                  renderSecureImagesThroughCF,
                  variantImage: backgroundRemovalEditSource,
              }),
              isBgRemovalActive ? MimeType.PNG : originalImageType,
          )
        : originalImageType;

    return {
        backgroundRemovalEditSource,
        inactiveVariantImageUrl,
        inactiveVariantImageType,
        imageType,
    };
};
